import { DumDataHoverDto, DumQueryApi } from '@src/Api/generated-global-event';
import { intToOrdinal } from '@src/Common/Helpers/intToOrdinal';
import { RootStore } from '@src/Store/Store';
import { makeAutoObservable } from 'mobx';
import { IDumFilter } from '../AdvanceFilter/AdvanceFilterStore';
import { OverviewType } from '../DUMEvents/DUMEvents.store';
import { IStatusApi, defaultStatusApi } from '../DUMEvents/DUMEvents.type';
export interface ICenturiesEvent {
  centuryName: string;
  data: ICenturiesData[];
}

export interface ICenturiesData {
  city: string;
  lat: number;
  lan: number;
  eventName: string;
  eventDate: Date;
  anomalyRating: number;
  type: string;
  id: string;
}

interface FilterDataTypes extends Omit<IDumFilter, 'year'> {
  searchValue?: string;
  year?: number;
}

const groupDataByCentury = (data: DumDataHoverDto[]): ICenturiesEvent[] => {
  const centuryGroups: ICenturiesEvent[] = [];
  data.forEach((item) => {
    if (item.dum_date) {
      const date = new Date(item.dum_date);
      const year = Math.floor(date.getFullYear() / 100) * 100;
      const centuryYear = Math.ceil(year / 100);
      const centuryName = `${intToOrdinal(centuryYear)} Century`;
      let currentCentury = centuryGroups.findIndex(
        (v) => v.centuryName === centuryName
      );
      if (currentCentury === -1) {
        centuryGroups.push({
          centuryName,
          data: []
        });
        currentCentury = centuryGroups.length - 1;
      }

      let eventType: 'casefile' | 'ismythic' | 'riftstorm' | 'event' =
        'casefile';
      let dumId = null;
      let dumName = null;
      let globalEvent = '';
      if (item.global_events && item.global_events.length > 0) {
        switch (item.global_events[0].product_type) {
          case 1:
            eventType = 'ismythic';
            break;
          case 2:
            eventType = 'riftstorm';
            break;
          case 3:
            eventType = 'event';
            break;

          default:
            break;
        }
        dumId = item.global_events[0].id;
        dumName = item.global_events[0].name;
        globalEvent = item.global_events[0].event_date ?? '';
      }
      centuryGroups[currentCentury].data.push({
        city: item.location?.location_name ?? 'Unknown location',
        lat: item.location?.latitude ?? 0,
        lan: item.location?.longitude ?? 0,
        eventName: dumName ?? '',
        eventDate: new Date(globalEvent),
        anomalyRating: 0,
        id: dumId || '',
        type: eventType
      });
    }
  });
  return Object.values(centuryGroups);
};

const PER_PAGE_DATA = 20;

export interface IAllEventStore {
  show: boolean;
  filters: FilterDataTypes;
  data: ICenturiesEvent[];
  allYears: number[];
  status: IStatusApi;
  _api: DumQueryApi | null;
  statusPreviousEvent: IStatusApi;
  canLoadMoreEvents: boolean;
  setAllYears: (allYear: OverviewType) => void;
  loadEvent: (_api: DumQueryApi) => Promise<void>;
  loadPreviousEvent: () => Promise<void>;
  handleSelectField: (fieldName: string, value: unknown) => void;
}

export class AllEventStore implements IAllEventStore {
  show = false;
  filters: FilterDataTypes = {
    year: undefined,
    location: undefined,
    contries: undefined,
    ecosystem: undefined,
    anomalyRating: undefined,
    searchValue: ''
  };
  statusPreviousEvent: IStatusApi = { ...defaultStatusApi, isLoading: false };
  canLoadMoreEvents = false;
  data: ICenturiesEvent[] = [];
  private rawData: DumDataHoverDto[] = [];
  private previousDumId: string | undefined = undefined;
  allYears: number[] = [];
  status: IStatusApi = defaultStatusApi;

  rootStore: RootStore;
  _api: DumQueryApi | null = null;
  constructor(rootStore: RootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  handleSelectField(fieldName: string, value: unknown) {
    this.filters = {
      ...this.filters,
      [fieldName]: value
    };
    if (this._api) {
      this.loadEvent(this._api);
    }
  }

  setAllYears(overviewData: OverviewType) {
    if (this.allYears.length === 0) {
      let years = overviewData.map((v) => v.year).filter(Boolean);
      years = years.filter(
        (value, index, array) => array.indexOf(value) === index
      );
      years.sort((a, b) => a - b);
      this.allYears = years;
    }
  }

  private checkCanLoadMore = <T>(data: T[]) => {
    if (data.length < PER_PAGE_DATA) {
      this.canLoadMoreEvents = false;
    } else {
      this.canLoadMoreEvents = true;
    }
  };

  async loadEvent(_api: DumQueryApi) {
    this._api = _api;

    if (this.filters.searchValue && this.filters.searchValue?.length < 3) {
      return;
    }

    this.status.isLoading = true;
    try {
      const results = await _api.dumGet(
        this.filters.year,
        undefined,
        this.filters.searchValue,
        undefined,
        this.filters.location,
        this.filters.contries?.filter((v) => v !== undefined),
        this.filters.ecosystem?.map((v) => v),
        this.filters.anomalyRating?.map((v) => v)
      );
      if (results && results.status === 200 && results.data) {
        const data: DumDataHoverDto[] = results.data.data as DumDataHoverDto[];
        if (data.length > 0) {
          this.rawData = data;
          this.previousDumId = data[data.length - 1].id ?? undefined;
          this.data = groupDataByCentury(this.rawData);
          this.checkCanLoadMore(data);
        }
      }
      const newStatus = {
        isSuccess: true,
        isLoading: false,
        isError: false,
        message: ''
      };
      this.status = newStatus;
    } catch (err) {
      if (err instanceof Error) {
        const checkErr = `${err}`.includes('undefined')
          ? 'Error, Something went wrong. Please refresh or try again later'
          : err;

        const newStatus = {
          isSuccess: false,
          isLoading: false,
          isError: true,
          message: `${checkErr}`
        };

        this.status = newStatus;
        this.data = [];
      }
    }
  }

  async loadPreviousEvent() {
    if (!this._api) {
      return;
    }

    if (this.filters.searchValue && this.filters.searchValue?.length < 3) {
      return;
    }

    this.statusPreviousEvent.isLoading = true;
    try {
      const results = await this._api.dumGet(
        this.filters.year,
        undefined,
        this.filters.searchValue,
        this.previousDumId,
        this.filters.location,
        this.filters.contries?.filter((v) => v !== undefined),
        this.filters.ecosystem?.map((v) => v),
        this.filters.anomalyRating?.map((v) => v)
      );
      if (results && results.status === 200 && results.data) {
        const data: DumDataHoverDto[] = results.data.data as DumDataHoverDto[];
        if (data.length > 0) {
          this.canLoadMoreEvents = true;
          this.previousDumId = data[data.length - 1].id ?? undefined;
          this.rawData = [...this.rawData, ...data];
          this.data = groupDataByCentury(this.rawData);
          this.checkCanLoadMore(data);
        } else {
          this.canLoadMoreEvents = false;
        }
      }
      const newStatus = {
        isSuccess: true,
        isLoading: false,
        isError: false,
        message: ''
      };
      this.statusPreviousEvent = newStatus;
    } catch (err) {
      if (err instanceof Error) {
        const checkErr = `${err}`.includes('undefined')
          ? 'Error, Something went wrong. Please refresh or try again later'
          : err;

        const newStatus = {
          isSuccess: false,
          isLoading: false,
          isError: true,
          message: `${checkErr}`
        };

        this.statusPreviousEvent = newStatus;
      }
    }
  }
}

export const AllEventStoreDefault: IAllEventStore = {
  show: false,
  filters: {} as FilterDataTypes,
  data: [],
  allYears: [],
  status: defaultStatusApi,
  _api: null,
  statusPreviousEvent: defaultStatusApi,
  canLoadMoreEvents: false,
  setAllYears: () => {
    //
  },
  handleSelectField: () => {
    //
  },
  async loadPreviousEvent() {
    //
  },
  async loadEvent() {
    //
  }
};
